import { Alert, Button, Spin } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import useMountEffect from '../../hooks/useMountEffect';
import { useFillViewportHeight } from './useFillViewportHeight';
import { withUserType } from '../withUserType';
import { EmptyState } from '../empty_state/EmptyState';
import WorkOrderTable from '../WorkOrderTable';
import Filters from '../WorkOrderTable/filters/filters';
import WorkOrderTableRowBuyer from '../WorkOrderTable/WorkOrderTableRow/buyer';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { AOPExecution, pageRows } from '../../types/AOPExecutionTypes';
import { isCancelledRead, readProcedureExecutions } from '../../thunks/agent_executions_thunks';
import AOPRunFunnel from './AOPRunFunnel';
import AOPRunState from './AOPRunState';
import {
	AOPRunState as AOPRunStateName,
	AOPRunStateCounts,
	AOPRunStateFilters,
	AOP_RUN_STATES,
	filtersForRunState,
	readRunStateCounts,
} from './utils/AOPExecutionStates';

const TC_NAME = 'aopExecutionWorkOrders';

/**
 * The table's header, down to its filters.
 *
 * `WorkOrderTable`'s own header leads with the status pipeline, and this
 * screen already has a funnel above it — Radar's five run states. The two look
 * alike and work differently: the funnel re-reads the runs and changes which
 * work orders reach the table at all, while a filter narrows the ones already
 * there. Two rows of tabs meaning different things is the control that would
 * mislead, which is why the pipeline is the part left out and not the filters.
 *
 * Filtering itself is sound. `ids` is a pre-applied filter and `parseURL`
 * spreads those last, so nothing picked here can widen the set past what Radar
 * has run on, and Clear resets to that set rather than to every work order.
 *
 * Saved views and export are left out too: a view carries filters between
 * tables, and this set is defined by one procedure, so a view saved here means
 * nothing anywhere else.
 *
 * Declared out here rather than inline — a new component identity on every
 * render would remount the rows.
 */
const AOPTableHeader = ({
	filters,
	applyFilters,
	applySorting,
	clearFilters,
	targetCollectionName,
	filterConfig,
	userType,
	sorters,
	preAppliedFilters,
	currentUser,
}: any) => (
	/*
	  No padding of its own. The funnel's `pb-m` already sets the gap above and
	  the table's own `marginTop` the gap below, so anything here doubles both —
	  and the tab's `px-xs` already insets this to where the funnel starts, so
	  any horizontal padding steps the filters in past the chevrons above them.
	*/
	<div>
		<Filters
			filters={filters}
			applyFilters={applyFilters}
			applySorting={applySorting}
			clearFilters={clearFilters}
			targetCollectionName={targetCollectionName}
			filterConfig={filterConfig ? filterConfig.filters : undefined}
			userType={userType}
			sorters={sorters}
			preAppliedFilters={preAppliedFilters}
			currentUser={currentUser}
		/>
	</div>
);

/**
 * How many pages of runs to walk before giving up.
 *
 * Every run has to be read, not just the first page: the table is driven by the
 * work-order ids behind the runs, so a run left unread is a work order missing
 * from the list. Capped rather than unbounded.
 *
 * Paging this lazily, the way the session lists do, is not open to us — the ids
 * are an argument to a second API, so a partial set would have the work-order
 * table report a count, a sort and an export over a prefix and present them as
 * the whole thing. What this screen actually wants is one answer giving the
 * distinct work orders and the newest run on each; the walk goes when that
 * lands.
 */
const MAX_PAGES = 10;
const PAGE_SIZE = 100;

/**
 * The Work Orders tab: the work orders this procedure has run on.
 *
 * The list is the app's own work-order table, so a work order reads, filters
 * and exports here exactly as it does everywhere else. What this screen adds is
 * the one column that is Radar's — what its run is doing — through the table's
 * `CustomWorkOrderRowDisplay` slot, the same way the linked work orders screen
 * adds its own.
 *
 * Which work orders appear is decided by Radar, not by the search: the runs are
 * read first and their work-order ids are handed to the table as a pre-applied
 * `ids` filter. A run, not a work order, is what has a state — a work order can
 * be reopened and run again — so the row reports the newest run on it.
 */

const AOPWorkOrdersTab: FC<any> = ({ match, loadExecutions, countExecutions }) => {
	const procedureId = nullSafeGet('params.id', match);
	// The tab body is white for every other tab, so the grey this screen sits on
	// is the screen's own and has to reach the bottom of the window itself.
	const [fillRef, fillHeight] = useFillViewportHeight(480);

	const [executions, setExecutions] = useState<AOPExecution[]>([]);
	/**
	 * Which of the five the funnel is narrowed to, and how many runs each holds.
	 *
	 * Both are the service's answers: a stage becomes `state` and `outcome` on
	 * the request, and a count is what it says matches rather than what came
	 * back on a page. Nothing is counted or filtered here.
	 */
	const [runState, setRunState] = useState<AOPRunStateName | null>(null);
	const [counts, setCounts] = useState<AOPRunStateCounts>({});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Reads the runs the service says are in scope, following the cursor.
	 *
	 * Every page has to be read because the table is driven by the work-order
	 * ids behind the runs, not because anything is being filtered here — which
	 * run belongs in the list is the service's answer, never ours.
	 */
	const load = useCallback(
		(stage: AOPRunStateName | null) => {
			setIsLoading(true);
			const collected: AOPExecution[] = [];
			// The stage becomes `state` and `outcome` on the request, so the walk
			// reads only the runs it is being asked about.
			const filters = stage ? filtersForRunState(stage) : {};

			const readFrom = (after: string | null, page: number): Promise<void> =>
				loadExecutions(procedureId, { ...filters, after, limit: PAGE_SIZE }).then((result) => {
					pageRows<AOPExecution>(result).forEach((execution) => collected.push(execution));
					return result.next_cursor && page + 1 < MAX_PAGES
						? readFrom(result.next_cursor, page + 1)
						: undefined;
				});

			return readFrom(null, 0)
				.then(() => {
					setExecutions(collected);
					setError(null);
					setIsLoading(false);
				})
				.catch((error) => {
					// A walk the next stage replaced. The screen belongs to that stage
					// now, so this one neither reports a failure nor takes the spinner
					// away from the read still running.
					if (isCancelledRead(error)) {
						return;
					}

					setError('Could not load what Radar has run.');
					setIsLoading(false);
				});
		},
		[loadExecutions, procedureId]
	);

	useMountEffect(() => {
		load(null);
		// One small request per stage, each keeping only the number.
		readRunStateCounts((filters) => countExecutions(procedureId, filters)).then(setCounts);
	});

	const onSelectStage = useCallback(
		(stage: AOPRunStateName | null) => {
			setRunState(stage);
			setExecutions([]);
			load(stage);
		},
		[load]
	);

	/** The work orders in scope, and the newest run on each of them. */
	const { ids, latestByWorkOrder } = useMemo(() => {
		const everyId: number[] = [];
		const newest: { [workOrderId: number]: AOPExecution } = {};

		executions.forEach((execution) => {
			if (everyId.indexOf(execution.work_order_id) < 0) {
				everyId.push(execution.work_order_id);
			}

			// A work order can be reopened and run again. The newest run is the one
			// still deciding anything, so that is the one the row reports.
			const held = newest[execution.work_order_id];
			if (!held || held.started_at < execution.started_at) {
				newest[execution.work_order_id] = execution;
			}
		});

		return { ids: everyId, latestByWorkOrder: newest };
	}, [executions]);

	/**
	 * The work-order row, with Radar's state where its actions would be.
	 *
	 * `CustomWorkOrderRowDisplay` replaces the whole row rather than adding a
	 * column to it, so this draws the real row and hands it `actions` — the slot
	 * on the right the row already has — exactly as the linked work orders
	 * screen does. Returning anything less costs the row its title, its chips
	 * and its status.
	 *
	 * The buyer row rather than a role switch: these screens are buyer-admin
	 * only. A work order can be reopened and run again, so the state reported is
	 * the newest run's — the one still deciding anything.
	 */
	const AOPWorkOrderRow = useCallback(
		({ record, currentUser, companyConfig, location, backText }) => {
			const execution = latestByWorkOrder[record.id];

			return (
				<WorkOrderTableRowBuyer
					record={record}
					currentUser={currentUser}
					companyConfig={companyConfig}
					location={location}
					backText={backText}
					actions={execution ? [<AOPRunState key="radar" execution={execution} />] : undefined}
				/>
			);
		},
		[latestByWorkOrder]
	);

	/*
	  The funnel stays put while a stage loads. Returning a bare spinner took it
	  off the screen on every click, so the thing you had just pressed vanished
	  and there was nothing to press next.
	*/
	const body = isLoading ? (
		<div className="flex justify-center p-l">
			<Spin />
		</div>
	) : null;

	return (
		/*
		  `-mt-m` closes the 16px the detail header leaves below itself. That gap
		  is white, like the tab body, so without it the grey starts a strip below
		  the tabs rather than against them.
		*/
		<div
			ref={fillRef}
			className="-mt-m flex flex-col bg-grey-3 px-xs py-m"
			style={{ minHeight: fillHeight }}
		>
			<div className="pb-m">
				<AOPRunFunnel counts={counts} selected={runState} onSelect={onSelectStage} />
			</div>

			{/*
			  A failure beside runs that did load: the rows are the answer and this
			  is about the stage that could not be read. With nothing loaded it
			  takes the place of the empty state instead — see below.
			*/}
			{error && ids.length > 0 ? (
				<div className="pb-m">
					<Alert type="error" showIcon message={error} />
				</div>
			) : null}

			{body}

			{/*
			  An empty `ids` filter is dropped before the request, which would show
			  every work order in the company rather than none. The table is only
			  mounted once there is something for it to be restricted to.
			*/}
			{isLoading || ids.length > 0 ? null : (
				/*
				  One block, whatever left the list empty.

				  A failed read used to put a boxed alert here instead, laid out
				  nothing like the empty state it stood in for — so a tab that could
				  not be read looked like a broken page rather than a list with
				  nothing in it. Same centred block either way; only the words change,
				  and a failure keeps its retry inside them.

				  They are still different claims. "Radar has not run this procedure
				  yet" is about the procedure, and a read that failed is exactly what
				  cannot establish it — so that sentence is only reached once a read
				  came back and brought nothing.

				  On the table's own white surface: this is the list being empty, not
				  the page being blank. Graphic, copy and height follow
				  WorkOrderTableEmptyState so it reads as the same table as everywhere
				  else in the app.
				*/
				<div className="bg-grey-1">
					<EmptyState
						graphic={
							error ? null : (
								<img
									alt=""
									style={{ marginBottom: 8 }}
									src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								/>
							)
						}
						headline={
							error
								? 'Could not load what Radar has run'
								: runState === null
								? 'Radar has not run this procedure yet'
								: `No ${AOP_RUN_STATES[runState].label.toLowerCase()} runs`
						}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									{error
										? 'This does not mean the procedure has never run — the runs could not be read.'
										: runState === null
										? 'A run starts when a work order matches this procedure\u2019s scenario.'
										: 'The runs this procedure has are under the other stages.'}
								</div>
								{error ? (
									<Button loading={isLoading} onClick={() => load(runState)}>
										Try again
									</Button>
								) : null}
							</div>
						}
						height={225}
					/>
				</div>
			)}

			{ids.length === 0 ? null : (
				/*
				  No surface of its own. `WorkOrderTable` puts the rows on white
				  itself, and anything here would also sit behind the filter bar it
				  draws above them — which belongs on the same grey as the funnel,
				  the way the work-order list has it. The list stays the one white
				  thing on the screen, and runs edge to edge: an inset card would
				  indent every row away from the rows it is a copy of.
				*/
				<div>
					<WorkOrderTable
						// The table reads its filters once on mount, so changing which
						// runs it is showing has to remount it.
						key={runState === null ? 'all' : runState}
						targetCollectionName={TC_NAME}
						preAppliedFilters={{ ids: ids.join(',') }}
						CustomWorkOrderRowDisplay={AOPWorkOrderRow}
						CustomHeader={AOPTableHeader}
						hideExportButton={true}
						// Nothing here draws the status pipeline, and the counts behind
						// it are their own request on every fetch and every stage change.
						ignoreRefreshCounts={true}
					/>
				</div>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	loadExecutions: (procedureId: string, params: any) =>
		/*
		  One in-flight walk for this screen. Picking another stage restarts it,
		  and a page belonging to the stage just left could otherwise land last and
		  be collected into the ids the table is narrowed to. The counts beside the
		  funnel pass no feed: they are five parallel reads of the same endpoint
		  and would cancel one another.
		*/
		dispatch(readProcedureExecutions(procedureId, params, `procedure_executions:${procedureId}`)),
	// One item is enough: all this wants is the `count` beside it, which is what
	// the filters match rather than what is on the page.
	countExecutions: (procedureId: string, filters: AOPRunStateFilters) =>
		dispatch(readProcedureExecutions(procedureId, { ...filters, limit: 1 })).then(
			(page: any) => page.count
		),
});

export default withUserType(
	withRouter<any, any>(connect(null, mapDispatchToProps)(AOPWorkOrdersTab))
);
